/**
 * Binance.US public WebSocket streaming — Phase 53.E.
 *
 * A single shared WebSocket connection, subscribed to the union of
 * every requested symbol. Consumers subscribe/unsubscribe via
 * `subscribe(symbols, kinds, onTick)` and the manager reference-counts
 * so the socket only stays open while at least one consumer needs it.
 *
 * Kinds:
 *   • ticker  — 24h rolling stats (updates every 1s per symbol)
 *   • trade   — every executed trade (highest-fidelity price feed)
 *   • kline_<tf> — candle updates (@kline_1h, @kline_1m …)
 *
 * The manager exposes a plain observer pattern — no React coupling —
 * so it can be used from anywhere (chart engine, panels, hooks).
 */

const WS_URL = 'wss://stream.binance.us:9443/stream';

class BinanceStreamManager {
  constructor() {
    this._ws = null;
    this._connecting = false;
    this._streams = new Map();          // stream-key -> refcount
    this._listeners = new Set();        // fn(msg)
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    this._pendingSubs = [];
    this._pendingUnsubs = [];
    this._msgId = 1;
    // Local cache — last seen frame per symbol per kind so late
    // subscribers get the current value immediately.
    this.cache = { ticker: new Map(), trade: new Map(), kline: new Map() };
  }

  _connect() {
    if (this._ws || this._connecting) return;
    this._connecting = true;
    try {
      this._ws = new WebSocket(WS_URL);
    } catch (e) {
      this._connecting = false;
      this._scheduleReconnect();
      return;
    }
    this._ws.onopen = () => {
      this._connecting = false;
      this._reconnectAttempts = 0;
      // Send SUBSCRIBE for every stream we currently track.
      const list = [...this._streams.keys()];
      if (list.length) this._send({ method: 'SUBSCRIBE', params: list, id: this._msgId++ });
    };
    this._ws.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }
      const d = msg?.data;
      const stream = msg?.stream;
      if (!d || !stream) return;
      if (stream.endsWith('@ticker')) {
        const row = {
          symbol:     d.s,
          last:       parseFloat(d.c),
          open:       parseFloat(d.o),
          high:       parseFloat(d.h),
          low:        parseFloat(d.l),
          change:     parseFloat(d.p),
          change_pct: parseFloat(d.P),
          volume:     parseFloat(d.v),
          quote_vol:  parseFloat(d.q),
          trade_count: parseInt(d.n || 0, 10),
          ts:         Date.now(),
        };
        this.cache.ticker.set(d.s, row);
        this._emit({ kind: 'ticker', symbol: d.s, row });
      } else if (stream.endsWith('@trade')) {
        const row = {
          symbol: d.s,
          price:  parseFloat(d.p),
          qty:    parseFloat(d.q),
          isBuyerMaker: !!d.m,
          ts:     d.T,
        };
        this.cache.trade.set(d.s, row);
        this._emit({ kind: 'trade', symbol: d.s, row });
      } else if (stream.includes('@kline_')) {
        const k = d.k;
        const row = {
          symbol: d.s,
          tf:     k.i,
          time:   Math.floor(k.t / 1000),
          open:   parseFloat(k.o),
          high:   parseFloat(k.h),
          low:    parseFloat(k.l),
          close:  parseFloat(k.c),
          volume: parseFloat(k.v),
          isClosed: !!k.x,
        };
        this.cache.kline.set(`${d.s}|${k.i}`, row);
        this._emit({ kind: 'kline', symbol: d.s, tf: k.i, row });
      }
    };
    this._ws.onclose = () => {
      this._ws = null;
      this._connecting = false;
      this._scheduleReconnect();
    };
    this._ws.onerror = () => {
      try { this._ws?.close(); } catch { /* noop */ }
    };
  }

  _scheduleReconnect() {
    if (this._streams.size === 0) return;  // nothing to reconnect to
    if (this._reconnectTimer) return;
    const delay = Math.min(20000, 500 * Math.pow(2, this._reconnectAttempts++));
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._connect();
    }, delay);
  }

  _send(obj) {
    if (this._ws && this._ws.readyState === 1) {
      try { this._ws.send(JSON.stringify(obj)); } catch { /* noop */ }
    }
  }

  _emit(msg) {
    for (const fn of this._listeners) {
      try { fn(msg); } catch { /* noop */ }
    }
  }

  _streamKey(symbol, kind, tf) {
    const s = symbol.toLowerCase();
    if (kind === 'kline') return `${s}@kline_${tf}`;
    return `${s}@${kind}`;
  }

  addListener(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  /**
   * subscribe(symbols, kinds='ticker', tf) — returns an unsubscribe
   * function that decrements refcounts and closes the socket when
   * nothing is left.
   */
  subscribe(symbols, kind = 'ticker', tf = '1m') {
    const keys = symbols.map((s) => this._streamKey(s, kind, tf));
    const toSub = [];
    for (const k of keys) {
      const cur = this._streams.get(k) || 0;
      if (cur === 0) toSub.push(k);
      this._streams.set(k, cur + 1);
    }
    if (!this._ws) this._connect();
    if (toSub.length && this._ws?.readyState === 1) {
      this._send({ method: 'SUBSCRIBE', params: toSub, id: this._msgId++ });
    }
    return () => {
      const toUnsub = [];
      for (const k of keys) {
        const cur = this._streams.get(k) || 0;
        if (cur <= 1) { this._streams.delete(k); toUnsub.push(k); }
        else this._streams.set(k, cur - 1);
      }
      if (toUnsub.length && this._ws?.readyState === 1) {
        this._send({ method: 'UNSUBSCRIBE', params: toUnsub, id: this._msgId++ });
      }
      if (this._streams.size === 0 && this._ws) {
        try { this._ws.close(); } catch { /* noop */ }
        this._ws = null;
      }
    };
  }
}

export const binanceStream = new BinanceStreamManager();
